import { Box, Button, Container, Grid, Link, Paper, Typography } from '@mui/material';
import BrandMark from './BrandMark';
import { Link as RouterLink } from 'react-router-dom';
import SeoHead from './SeoHead';
import type { FaqItem } from './SeoHead';

type PageSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

type FeatureItem = {
  title: string;
  description: string;
};

type InternalLinkItem = {
  label: string;
  to: string;
};

type SeoLandingPageProps = {
  title: string;
  description: string;
  canonicalUrl: string;
  pagePath: string;
  h1: string;
  heroSubtitle: string;
  intro: string;
  keyBenefits: string[];
  problemSolution: PageSection;
  featureHighlights: FeatureItem[];
  whyWorkflowEstate: string[];
  deepDive?: PageSection;
  audienceFit?: PageSection;
  faqItems: FaqItem[];
  ctaHeading: string;
  ctaBody: string;
  ctaPrimaryText: string;
  ctaPrimaryTo: string;
  ctaSecondaryText: string;
  ctaSecondaryTo: string;
  internalLinks: InternalLinkItem[];
};

const sectionCardSx = {
  p: { xs: 3, md: 4 },
  borderRadius: 3,
  border: '1px solid',
  borderColor: '#e5e7eb',
  boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
  bgcolor: '#ffffff',
  color: '#111827',
};

const SeoLandingPage = ({
  title,
  description,
  canonicalUrl,
  pagePath,
  h1,
  heroSubtitle,
  intro,
  keyBenefits,
  problemSolution,
  featureHighlights,
  whyWorkflowEstate,
  deepDive,
  audienceFit,
  faqItems,
  ctaHeading,
  ctaBody,
  ctaPrimaryText,
  ctaPrimaryTo,
  ctaSecondaryText,
  ctaSecondaryTo,
  internalLinks,
}: SeoLandingPageProps) => {
  const breadcrumbItems = [
    { name: 'Home', url: 'https://workflow.estate/' },
    { name: h1, url: canonicalUrl },
  ];

  return (
    <>
      <SeoHead
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        pagePath={pagePath}
        faqItems={faqItems}
        breadcrumbItems={breadcrumbItems}
      />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, color: '#111827', bgcolor: '#f8fafc' }}>
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            underline="none"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, color: '#0f172a' }}
            aria-label="Go to Workflow.Estate home page"
          >
            <BrandMark size={32} alt="Workflow.Estate logo" />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
              Workflow.Estate
            </Typography>
          </Link>
          <Button component={RouterLink} to="/" variant="text">
            Main page
          </Button>
        </Box>

        <Box sx={{ mb: { xs: 4, md: 6 }, color: '#111827' }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: '#475569' }}>
            Built for Australian real estate agencies
          </Typography>
          <Typography component="h1" variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
            {h1}
          </Typography>
          <Typography variant="h6" sx={{ color: '#334155', mb: 2, maxWidth: 900 }}>
            {heroSubtitle}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 900 }}>
            {intro}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button variant="contained" component={RouterLink} to={ctaPrimaryTo}>
              {ctaPrimaryText}
            </Button>
            <Button variant="outlined" component={RouterLink} to={ctaSecondaryTo}>
              {ctaSecondaryText}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ ...sectionCardSx, mb: 4 }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Key Benefits
          </Typography>
          <Grid container spacing={1.5}>
            {keyBenefits.map((benefit) => (
              <Grid item xs={12} md={6} key={benefit}>
                <Typography variant="body1">- {benefit}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper sx={{ ...sectionCardSx, mb: 4 }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {problemSolution.heading}
          </Typography>
          {problemSolution.paragraphs.map((paragraph) => (
            <Typography key={paragraph} variant="body1" sx={{ mb: 1.5 }}>
              {paragraph}
            </Typography>
          ))}
          {problemSolution.bullets?.map((bullet) => (
            <Typography key={bullet} variant="body1">
              - {bullet}
            </Typography>
          ))}
        </Paper>

        <Box sx={{ mb: 4 }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Feature Highlights
          </Typography>
          <Grid container spacing={2}>
            {featureHighlights.map((feature) => (
              <Grid item xs={12} md={6} key={feature.title}>
                <Paper sx={sectionCardSx}>
                  <Typography component="h3" variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper sx={{ ...sectionCardSx, mb: 4 }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Why Workflow.Estate
          </Typography>
          {whyWorkflowEstate.map((item) => (
            <Typography key={item} variant="body1" sx={{ mb: 1 }}>
              - {item}
            </Typography>
          ))}
        </Paper>

        {deepDive && (
          <Paper sx={{ ...sectionCardSx, mb: 4 }}>
            <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {deepDive.heading}
            </Typography>
            {deepDive.paragraphs.map((paragraph) => (
              <Typography key={paragraph} variant="body1" sx={{ mb: 1.5 }}>
                {paragraph}
              </Typography>
            ))}
            {deepDive.bullets?.map((bullet) => (
              <Typography key={bullet} variant="body1">
                - {bullet}
              </Typography>
            ))}
          </Paper>
        )}

        {audienceFit && (
          <Paper sx={{ ...sectionCardSx, mb: 4 }}>
            <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              {audienceFit.heading}
            </Typography>
            {audienceFit.paragraphs.map((paragraph) => (
              <Typography key={paragraph} variant="body1" sx={{ mb: 1.5 }}>
                {paragraph}
              </Typography>
            ))}
            {audienceFit.bullets?.map((bullet) => (
              <Typography key={bullet} variant="body1">
                - {bullet}
              </Typography>
            ))}
          </Paper>
        )}

        <Paper sx={{ ...sectionCardSx, mb: 4 }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            FAQs
          </Typography>
          {faqItems.map((faq) => (
            <Box key={faq.question} sx={{ mb: 2 }}>
              <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 700 }}>
                {faq.question}
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569' }}>
                {faq.answer}
              </Typography>
            </Box>
          ))}
        </Paper>

        <Paper sx={{ ...sectionCardSx, mb: 4, bgcolor: '#eff6ff' }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
            {ctaHeading}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {ctaBody}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button variant="contained" component={RouterLink} to={ctaPrimaryTo}>
              {ctaPrimaryText}
            </Button>
            <Button variant="outlined" component={RouterLink} to={ctaSecondaryTo}>
              {ctaSecondaryText}
            </Button>
          </Box>
        </Paper>

        <Paper sx={sectionCardSx}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Related Pages
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {internalLinks.map((link) => (
              <Link key={link.to} component={RouterLink} to={link.to} underline="hover" sx={{ color: 'primary.main' }}>
                {link.label}
              </Link>
            ))}
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default SeoLandingPage;
